import type { ReactNode } from 'react'
import { useAutosave } from '@/core/hooks/useAutosave'
import { AutosaveStatus } from '@/core/components/form/AutosaveBoundary'
import { ModalHead, ScrollBar, toastError } from 'admination-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useApp } from '@/core/state/appStore'
import { setOwner, updatePendingMember, type Member, type PendingMemberPatch } from '@/core/data/members'
import type { DashboardCoaching } from '@/core/types'
import { memberDisplayName } from '@/core/lib/orgMembers'
import { formatDatum } from '@/core/lib/dates'
import { FormRenderer } from '@/core/components/form/FormRenderer'
import { initialFormValues, updateFormField, type FormValues } from '@/core/components/form/formModel'
import { memberDetailsForm } from '@/core/templates'
import type { Tab } from '@/core/templates/types'
import { Checkbox } from 'admination-react'
import { friendlyErrorMessage } from '@/core/lib/dbError'
import { Tabs, type TabsItem } from 'admination-react'
import { ListView } from '@/core/views/ListView'
import { memberCoachingsViewConfig, coachingsSummary, coachingsForMember } from './memberCoachingsViewConfig'

const MEMBER_TABS: Tab[] = [
  { id: 'mitglied', label: 'Mitglied', icon: 'i-person' },
  { id: 'coachings', label: 'Coachings', icon: 'i-doc' },
]

function roleLabel(m: Member): string {
  const roles = [m.isOwner ? 'Owner' : null, m.isCoach ? 'Coach' : null].filter((x): x is string => !!x)
  return roles.join(', ') || '-'
}

const EDITABLE_FIELDS: Record<string, keyof PendingMemberPatch> = {
  Vorname: 'vorname',
  Nachname: 'nachname',
  'E-Mail': 'email',
}

function valuesFor(m: Member): Record<string, unknown> {
  return {
    Mitglied: { Vorname: m.vorname ?? '', Nachname: m.nachname ?? '', 'E-Mail': m.email ?? '' },
    'Rolle & Status': {
      Rolle: roleLabel(m),
      Status: m.authUserId === null ? 'Ausstehend' : 'Aktiv',
      'Mitglied seit': formatDatum(m.createdAt),
      'Eingeladen von': m.invitedByName,
    },
  }
}

export function MemberDetails({ userId, actions }: { userId: string | null; actions?: ReactNode }) {
  const { state, dispatch, reloadMembers, coachings, identity, members } = useApp()
  const scrollerRef = useRef<HTMLElement>(null)
  const member = members.find((m) => m.userId === userId) ?? null

  const myCoachings = useMemo(() => coachingsForMember(coachings, member), [coachings, member])
  const coachingsHeader = useMemo(
    () => <p className="field-hint is-info">{coachingsSummary(myCoachings)}</p>,
    [myCoachings],
  )

  const isOwner = identity?.isOwner ?? false

  const [values, setValues] = useState<FormValues | null>(() =>
    member ? initialFormValues(memberDetailsForm, valuesFor(member)) : null,
  )

  const lastUserId = useRef<string | null | undefined>(undefined)

  useEffect(() => {
    if (!member) return
    if (lastUserId.current !== userId || member.authUserId !== null) {

      lastUserId.current = userId
      const v = initialFormValues(memberDetailsForm, valuesFor(member))
      setValues(v)
    } else {

      const roleStatus = {
        Rolle: roleLabel(member),
        Status: member.authUserId === null ? 'Ausstehend' : 'Aktiv',
        'Mitglied seit': formatDatum(member.createdAt),
        'Eingeladen von': member.invitedByName,
      }
      setValues((prev) => (prev ? { ...prev, 'Rolle & Status': roleStatus } : prev))
    }
  }, [member, userId])

  const close = () => dispatch({ type: 'CLOSE' })

  const openCoaching = (coaching: DashboardCoaching) => {
    dispatch({ type: 'SET_CONTROL', key: 'selectedCoachingId', value: coaching.id })
    dispatch({ type: 'SELECT_COACHING_DOC', name: null })
    const source = { kind: 'row' as const }
    dispatch({ type: 'OPEN_SCREEN', screen: { kind: 'personal', tab: 'details', coachingId: coaching.id, source } })
  }

  const activeTab = state.screen?.tab === 'coachings' ? 'coachings' : 'mitglied'

  const pending = member?.authUserId === null
  const editable = isOwner && pending

  const setField = (path: string, v: unknown) => {
    setValues((prev) => updateFormField(prev, path, v))
  }

  const switchOwner = (next: boolean) => {
    if (!member) return
    setOwner(member.userId, next)
      .then(() => reloadMembers())
      .catch((e) => {
        console.error('Owner-Wechsel fehlgeschlagen:', e)
        toastError(friendlyErrorMessage(e, 'Die Rolle konnte nicht geändert werden.'))
      })
  }

  const editableValues = useMemo(() => {
    if (!values) return null
    return Object.fromEntries(Object.entries(EDITABLE_FIELDS).map(([field, key]) => [key, String(values.Mitglied[field] ?? '').trim()])) as PendingMemberPatch
  }, [values])
  const commit = (patch: PendingMemberPatch | null, baseline: PendingMemberPatch | null | undefined) => {
    if (!patch || !member || !editable) return
    const changed = Object.fromEntries(Object.entries(patch).filter(([key, value]) => baseline?.[key as keyof PendingMemberPatch] !== value))
    if (!Object.keys(changed).length) return
    return updatePendingMember(member.userId, changed)
      .then(() => reloadMembers())
      .catch((e) => {
        console.error('Mitglied speichern fehlgeschlagen:', e)
        throw e
      })
  }

  useAutosave(editableValues, !!editableValues && !!member && editable, commit, 250, {
    form: 'member-details', record: userId ?? undefined, label: member ? memberDisplayName(member) : 'Mitglied',
    recover: (patch) => setValues((previous) => previous && patch ? {
      ...previous,
      Mitglied: { ...previous.Mitglied, ...Object.fromEntries(Object.entries(EDITABLE_FIELDS).map(([field, key]) => [field, patch[key] ?? ''])) },
    } : previous),
  })

  return (
    <>
      <ModalHead titleIcon="i-person" center={<><div data-tab-bar>
          <Tabs
            items={MEMBER_TABS.map((t): TabsItem => ({ name: t.id, label: t.label, icon: t.icon }))}
            activeName={activeTab}
            onChange={(id) => dispatch({ type: "CHANGE_TAB", tab: id })}
          />
        </div></>} title={member ? memberDisplayName(member) : 'Mitglied'} actions={<><AutosaveStatus />{actions}</>} onClose={close} closeLabel="Schließen" />
      {activeTab === 'coachings' ? (
        <ListView
          items={myCoachings}
          config={memberCoachingsViewConfig}
          viewState={{}}
          header={coachingsHeader}
          selectedKey={state.screen?.coachingId ?? state.selectedCoachingId}
          onRowClick={openCoaching}
        />
      ) : (
        <main
          className="form" data-scroll-host ref={scrollerRef}

          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLElement).blur()
          }}
        >
          {values && (
            <FormRenderer definition={memberDetailsForm} values={values} onChange={setField} readOnly={!editable} />
          )}
          {isOwner && member && !pending && (
            <div className="section">
              <h3 className="section-header">Berechtigung</h3>
              <Checkbox checked={member.isOwner} onChange={(e) => switchOwner(e.target.checked)}>
                Owner dieser Organisation
              </Checkbox>
            </div>
          )}
        </main>
      )}
      {activeTab === 'mitglied' && <ScrollBar scrollerRef={scrollerRef} />}
    </>
  )
}
